import i18n from '@/utils/i18n';
import PaymentHistoryEmptyStateIcon from '@assets/icons/payment-history-empty-state.svg';
import React, { useEffect } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { createStyles } from '@/contexts/theme-context';
import { EmptyStateMinimal } from '@/components/empty-state-minimal';
import { OfflineComponent } from '@/components/offline-component';
import { PaymentHistoryList } from '@/components/payments/payment-history-list';
import { PaymentHistoryListFieldsFragment } from '@/types';
import { RefreshControl, Text, View } from 'react-native';
import { Spacing } from '@usepepper/sprout-kit';
import { TYPOGRAPHY } from '@/styles';
import { useGetPaymentHistoryQuery } from '@/hooks/use-get-payment-history-query';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useUniqueErrorToast } from '@/hooks/use-unique-toasts';

interface Props {
  contentSpacing?: Spacing;
  renderListItem: (
    payment: PaymentHistoryListFieldsFragment,
  ) => React.ReactElement | null;
}

export const PaymentHistoryComponent: React.FC<Props> = ({
  contentSpacing = Spacing.SPACE_MEDIUM,
  renderListItem,
}) => {
  const [styles] = useStyles();
  const showErrorToast = useUniqueErrorToast();
  const {
    error,
    fetchMore,
    fetchingMore,
    hasResponse,
    loading,
    paymentBatches,
    refresh,
    refreshing,
  } = useGetPaymentHistoryQuery();
  const isOfflineModeActive = useOfflineModeActive();

  useEffect(() => {
    if (error) {
      showErrorToast();
    }
  }, [error, showErrorToast]);

  return loading && !fetchingMore && !hasResponse && !refreshing ? (
    <View style={styles.loadingContainer}>
      <ActivityIndicator />
    </View>
  ) : !hasResponse && isOfflineModeActive ? (
    <OfflineComponent />
  ) : paymentBatches.length ? (
    <PaymentHistoryList
      ListFooterComponent={fetchingMore ? <ActivityIndicator /> : null}
      listSeparatorSpacing={contentSpacing}
      onEndReached={fetchMore}
      paymentBatches={paymentBatches}
      refreshControl={
        <RefreshControl onRefresh={refresh} refreshing={refreshing} />
      }
      renderListItem={renderListItem}
    />
  ) : (
    <EmptyStateMinimal
      children={
        <Text style={[TYPOGRAPHY.medium, styles.emptyStateText]}>
          {i18n.t('payment_history_empty_message')}
        </Text>
      }
      containerStyle={styles.emptyStateContainer}
      ImageComponent={PaymentHistoryEmptyStateIcon}
      text={i18n.t('payment_history_empty_title')}
      textStyle={TYPOGRAPHY.h2}
    />
  );
};

const useStyles = createStyles((theme) => ({
  emptyStateContainer: {
    backgroundColor: theme.sk.color.bg.primary.default,
  },
  emptyStateText: {
    lineHeight: 24,
    marginTop: theme.sk.spacing.spaceSMedium,
    textAlign: 'center',
  },
  loadingContainer: {
    marginVertical: theme.sk.spacing.spaceMedium,
  },
}));
